/**
 * File-extension helpers for Candid document preview renderers.
 *
 * @module getDocumentPreviewKind
 */

/** Image extensions rendered with the zoomable image panel. */
const IMAGE_EXTENSIONS = new Set(["png", "jpg", "jpeg", "gif", "webp"]);

/**
 * Returns the lowercased file extension from a path or URL.
 *
 * @param path - GCS object path or preview URL
 * @returns Extension without the dot, or an empty string
 */
export const getPathExtension = (path: string): string => {
  const fileName = path.split("/").pop() ?? "";
  const withoutQuery = fileName.split("?")[0];
  const dotIndex = withoutQuery.lastIndexOf(".");
  if (dotIndex < 0) {
    return "";
  }
  return withoutQuery.slice(dotIndex + 1).toLowerCase();
};

/**
 * Returns true when the path should use {@link PdfRenderer}.
 *
 * @param path - GCS object path or preview URL
 * @returns Whether the file is a PDF
 */
export const isPdfDocumentPath = (path: string): boolean =>
  getPathExtension(path) === "pdf";

/**
 * Returns true when the path should use the image viewer panel.
 *
 * @param path - GCS object path or preview URL
 * @returns Whether the file is a raster image
 */
export const isImageDocumentPath = (path: string): boolean =>
  IMAGE_EXTENSIONS.has(getPathExtension(path));
